import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { Dialog, Button, Dropdown, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import { calculationCodeTypes } from "@utils/constants";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
} from "./AddEditPaymentTerms.styled";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditPaymentTerms = (props) => {
  const {
    t,
    id,
    isEditMode,
    title,
    onClose,
    updateRefeshStatus,
    selectedPaymentTerm,
  } = props;
  const [isPaymentTermCreating, setIsPaymentTermCreating] = useState(false);
  const [createPaymentTermAPIError, setCreatePaymentTermAPIError] =
    useState("");

  const generalParams = useGetGeneralParameters();

  const [formData, setFormData] = useState({
    label: {
      required: true,
      value: selectedPaymentTerm?.label ?? "",
      error: "",
    },
    calculationValue: {
      required: true,
      value: selectedPaymentTerm?.calculationValue ?? "",
      error: "",
    },
    calculationCode: {
      required: true,
      value: selectedPaymentTerm?.calculationCode ?? "",
      error: "",
    },
    country: {
      required: true,
      value: selectedPaymentTerm?.country?.id ?? "",
      error: "",
    },
    isActive: {
      required: true,
      value: selectedPaymentTerm?.isActive ?? true,
      error: "",
    },
  });

  const onCountryChange = (e) => {
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onCalculationCodeChange = (e) => {
    setFormData({
      ...formData,
      calculationCode: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onLabelChange = (e) => {
    setFormData({
      ...formData,
      label: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onpaymentTermCalculationValueChange = (e) => {
    setFormData({
      ...formData,
      calculationValue: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.country.value) {
      isFormValid = false;
      data.country.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.label.value) {
      isFormValid = false;
      data.label.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.calculationCode.value) {
      isFormValid = false;
      data.calculationCode.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.calculationValue.value) {
      isFormValid = false;
      data.calculationValue.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (
      data.calculationValue.value &&
      !/^-?\d*?$/.test(data.calculationValue.value)
    ) {
      isFormValid = false;
      data.calculationValue.error = t("GENERAL_FORMAT_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const data = {
      id: isEditMode ? selectedPaymentTerm.id : "",
      label: formData.label.value,
      calculationCode: formData.calculationCode.value,
      calculationValue: formData.calculationValue.value,
      isActive: formData.isActive.value,
      country: JSON.stringify({
        id: formData.country.value,
      }),
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsPaymentTermCreating(true);
      setCreatePaymentTermAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post("/general/paymentTerms", getFormData())
          .then(() => {
            setIsPaymentTermCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsPaymentTermCreating(false);
            setCreatePaymentTermAPIError(error.response?.data?.message ?? "");
          });
      }
      if (id && isEditMode) {
        axios
          .put(`/general/paymentTerms/${id}`, getFormData())
          .then(() => {
            setIsPaymentTermCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsPaymentTermCreating(false);
            setCreatePaymentTermAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <Dialog width="33rem">
      <div>
        <HeaderWrapper>
          <Title>
            {title} {id ? "#" + id : ""}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr">
                    <Dropdown
                      id="payterm-term-country"
                      label={t("COUNTRY")}
                      name="country"
                      value={formData.country.value}
                      onChange={onCountryChange}
                      defaultOption={t("COUNTRY")}
                      options={generalParams.countryList}
                      labelStyles={
                        formData.country.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.country.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.country.error}
                    />
                  </GridRow>

                  <GridRow column="1fr">
                    <TextInput
                      id="label"
                      type="text"
                      label={t("LABEL")}
                      isFloat
                      required
                      value={String(formData.label.value)}
                      onChange={(e) => onLabelChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.label.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.label.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.label.error}
                      placeholder={t("LABEL")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="payterm-term-calculation_code"
                      label={t("PAYMENT_TERM_CALCULATION_CODE")}
                      name="calculation_code"
                      value={formData.calculationCode.value}
                      onChange={onCalculationCodeChange}
                      defaultOption={t("PAYMENT_TERM_CALCULATION_CODE")}
                      options={calculationCodeTypes}
                      labelStyles={
                        formData.calculationCode.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.calculationCode.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.calculationCode.error}
                    />
                    <TextInput
                      id="payterm-term_calculation_value"
                      type="text"
                      label={t("PAYMENT_TERM_CALCULATION_VALUE")}
                      isFloat
                      required
                      value={String(formData.calculationValue.value)}
                      onChange={(e) => onpaymentTermCalculationValueChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.calculationValue.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.calculationValue.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.calculationValue.error}
                      placeholder={t("PAYMENT_TERM_CALCULATION_VALUE")}
                    />
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createPaymentTermAPIError && <div>{createPaymentTermAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isPaymentTermCreating && <ClipLoader size={15} color="#fff" />}
              {!isPaymentTermCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditPaymentTerms.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditPaymentTerms.defaultProps = {
  id: null,
  isEditMode: false,
};
export default withTranslation()(AddEditPaymentTerms);
